import { ITrackedClass } from '@models/interfaces';
import { Token } from '../token';
import { Validator } from '../validator';
import { TrackChangesOptions } from './change-tracker';
import { IDecoratedModel } from './kb3d-decorator';

export type Constructor<T> = new (...args: any[]) => T;
// eslint-disable-next-line @typescript-eslint/ban-types
export type AbstractConstructor<T> = Function & { prototype: T };

export interface IKb3dModel<T> extends TrackChangesOptions<T> {
    token: Token;
    classConstructor: Constructor<T> | AbstractConstructor<T>;
    /**
     * the function to create the class used by the manager.
     * If provided, you will be passed an anonymous object in the shape
     * of the class that can be used to create the instance.
     * If not provided, the constructor of the class will be called by
     * the manager with no arguments.
     */
    create?: (args: Partial<T>) => T;
    afterCreate?: (instance: T) => void;
    /**
     * Hook that happens while serializing this model to JSON.
     * You are passed the instance and the default serialization of the model,
     * which you can modify.
     * Return true to include the instance in the serialization, or false to omit it
     */
    onSerializing?: (instance: T, serializedObject: Partial<T>) => boolean;

    /** if trackChanges is true, the model will be wrapped in the TrackChanges decorator */
    trackChanges: boolean;

    validate?: (node: ITrackedClass<T>, v: Validator) => void;
}

export const Kb3dModels = new Map<Token, IKb3dModel<any>>();

/**
 * Curry function to filter items in an array to items of a token type
 * @param classKey String key of the class you want to see if the instance is an instance of
 */
export function ofToken<T>(token: Token<T>) {
    return (instance: any): instance is T => instanceOf(instance, token);
}

/**
 * Checks if the instance of a class given is an instance of the class represented by it's
 * injectable class key.  If the instance is an instance of a class that was not
 * decorated, or if the class represented by the key was not decorated, then this will
 * return false!
 * @param instance An instance of a class that was decorated
 * @param classKey String key of the class you want to see if the instance is an instance of
 */
export function instanceOf<T>(instance: any, token: Token<T>): instance is T {
    if (!instance) return false;

    const model = Kb3dModels.get(token);
    return model ? instance instanceof model.classConstructor : false;
}

/** Curry-able version of instanceOf */
export function isInstanceOf<T>(token: Token<T>) {
    return (instance: any): instance is T => instanceOf(instance, token);
}

export function getToken<T>(object: T): Token<T> | undefined {
    const node = object as unknown as IDecoratedModel;
    if (node.$type) {
        return Token.tryWithKey(node.$type);
    }
}

/**
 * Checks if the instance of a class given is an instance of the class represented by it's
 * injectable class key.  If the instance is an instance of a class that was not
 * decorated, or if the class represented by the key was not decorated, then this will
 * return false!
 * @param instance An instance of a class that was decorated
 * @param classKey String key of the class you want to see if the instance is an instance of
 */
export function instanceOfAny<T>(instance: any, ...tokens: Token[]): instance is T {
    if (!instance) return false;

    return tokens.some(t => instanceOf(instance, t));
}
